import React, { useEffect, useState } from 'react';
import { Phone, Menu, X, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Logo } from './Logo';
import { SITE } from '../data/site';
import { TESTIDS } from '../constants/testIds';

const NAV = [
  { label: 'About', href: '/#about', tid: 'nav-about' },
  {
    label: 'Services',
    href: '/#services',
    tid: TESTIDS.navServices,
    children: [
      { label: 'All Services', href: '/#services', tid: 'nav-services-all' },
      {
        label: 'Gutter Cleaning Leamington Spa',
        href: '/gutter-cleaning-leamington-spa',
        tid: 'nav-gutter-cleaning-leam',
        route: true,
      },
    ],
  },
  { label: 'Gallery', href: '/#gallery', tid: TESTIDS.navGallery },
  { label: 'Areas', href: '/#areas', tid: TESTIDS.navAreas },
  { label: 'Reviews', href: '/#reviews', tid: TESTIDS.navReviews },
  { label: 'FAQ', href: '/faq', tid: 'nav-faq', route: true },
  { label: 'Contact', href: '/#contact', tid: TESTIDS.navContact },
];

const desktopLinkClass =
  'font-display uppercase tracking-widest text-sm text-[#FDFBF7] hover:text-[#E8B842] transition-colors';
const mobileLinkClass =
  'font-display uppercase tracking-widest text-base text-[#FDFBF7] hover:text-[#E8B842] py-3 border-b border-[#1A1A1A]';
const mobileChildClass =
  'font-display uppercase tracking-wider text-sm text-[#E8B842] hover:text-white py-2 pl-5 border-b border-[#1A1A1A]';

const NavLink = ({ item, onClick, className }) =>
  item.route ? (
    <Link to={item.href} onClick={onClick} data-testid={item.tid} className={className}>
      {item.label}
    </Link>
  ) : (
    <a href={item.href} onClick={onClick} data-testid={item.tid} className={className}>
      {item.label}
    </a>
  );

const DesktopDropdown = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid={item.tid}
        aria-expanded={open}
        aria-haspopup="menu"
        className={`${desktopLinkClass} inline-flex items-center gap-1`}
      >
        {item.label}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className={`absolute left-0 top-full pt-3 min-w-[280px] transition-all duration-150 ${
          open
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-1'
        }`}
        role="menu"
      >
        <div className="bg-[#050505] border border-[#E8B842]/25 shadow-2xl">
          {item.children.map((c) =>
            c.route ? (
              <Link
                key={c.href}
                to={c.href}
                onClick={() => setOpen(false)}
                data-testid={c.tid}
                className="block px-5 py-3.5 font-display uppercase tracking-wider text-xs text-[#FDFBF7] hover:text-[#E8B842] hover:bg-[#0E0E0E] border-b border-[#1A1A1A] last:border-b-0 transition-colors"
              >
                {c.label}
              </Link>
            ) : (
              <a
                key={c.href}
                href={c.href}
                onClick={() => setOpen(false)}
                data-testid={c.tid}
                className="block px-5 py-3.5 font-display uppercase tracking-wider text-xs text-[#FDFBF7] hover:text-[#E8B842] hover:bg-[#0E0E0E] border-b border-[#1A1A1A] last:border-b-0 transition-colors"
              >
                {c.label}
              </a>
            ),
          )}
        </div>
      </div>
    </div>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <header
      data-testid={TESTIDS.header}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#050505]/95 backdrop-blur-md border-b border-[#E8B842]/20'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          <a href="/#top" data-testid={TESTIDS.navLogo} className="flex-shrink-0">
            <Logo height={64} />
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) =>
              n.children ? (
                <DesktopDropdown key={n.label} item={n} />
              ) : (
                <NavLink key={n.href} item={n} className={desktopLinkClass} />
              ),
            )}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={SITE.phoneTel}
              data-testid={TESTIDS.headerCallBtn}
              className="hidden sm:inline-flex items-center gap-2 bg-[#E8B842] hover:bg-[#F4C534] text-black font-display font-bold uppercase tracking-wider px-5 py-3 text-sm transition-colors"
            >
              <Phone className="w-4 h-4" />
              {SITE.phoneDisplay}
            </a>
            <a
              href={SITE.phoneTel}
              data-testid="header-call-mobile"
              className="sm:hidden inline-flex items-center justify-center w-11 h-11 bg-[#E8B842] text-black"
              aria-label="Call Luke"
            >
              <Phone className="w-5 h-5" />
            </a>
            <button
              type="button"
              data-testid={TESTIDS.mobileMenuToggle}
              onClick={() => setOpen(!open)}
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 border border-[#E8B842]/30 text-[#E8B842]"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#050505] border-t border-[#E8B842]/20">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col">
            {NAV.map((n) => (
              <React.Fragment key={n.label}>
                <NavLink item={n} onClick={close} className={mobileLinkClass} />
                {n.children &&
                  n.children.map(
                    (c) =>
                      c.href !== n.href && (
                        <NavLink
                          key={c.href}
                          item={c}
                          onClick={close}
                          className={mobileChildClass}
                        />
                      ),
                  )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
